// src/pages/DashboardImproved.tsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../components/ui/Card";
import Header from "../components/layout/Header";
import { useToast } from "../hooks/useToast";
import {
  getTotalVehicles,
  getTotalOutstanding,
  getMonthlyInvoices,
  getMonthlyPayments,
  getMonthlyWriteOffs
} from "../services/api";

interface DashboardStats {
  totalVehicles: number;
  totalOutstanding: number;
  monthlyInvoices: number;
  monthlyPayments: number;
  monthlyWriteOffs: number;
}

interface QuickAction {
  title: string;
  description: string;
  icon: string;
  color: string;
  path: string;
  roles?: string[];
}

const DashboardImproved: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const { showError, ToastContainer } = useToast();

  // Parse stored user object
  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;

  const quickActions: QuickAction[] = [
    {
      title: "Vehicle Search",
      description: "Search and view vehicle details",
      icon: "🔍",
      color: "bg-blue-600",
      path: "/vehicles"
    },
    {
      title: "Register Vehicle", 
      description: "Add a new vehicle to the system",
      icon: "➕",
      color: "bg-indigo-600",
      path: "/register-vehicle",
      roles: ["ADMIN", "STAFF"]
    },
    {
      title: "Create Invoice",
      description: "Generate new invoices",
      icon: "📄",
      color: "bg-green-600", 
      path: "/invoices",
      roles: ["ADMIN", "STAFF"]
    },
    {
      title: "Collect Payments",
      description: "Process vehicle payments",
      icon: "💳",
      color: "bg-yellow-600",
      path: "/payments",
      roles: ["ADMIN", "STAFF"]
    },
    {
      title: "Write-Offs",
      description: "Manage debt write-offs",
      icon: "✍️",
      color: "bg-red-600",
      path: "/writeoffs",
      roles: ["ADMIN", "STAFF"]
    },
    {
      title: "Reports",
      description: "View system reports and analytics",
      icon: "📊",
      color: "bg-purple-600",
      path: "/reports"
    }
  ];

  const fetchDashboardStats = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [vehicles, outstanding, invoices, payments, writeOffs] = await Promise.all([
        getTotalVehicles(),
        getTotalOutstanding(),
        getMonthlyInvoices(new Date().getMonth() + 1, new Date().getFullYear()),
        getMonthlyPayments(new Date().getMonth() + 1, new Date().getFullYear()),
        getMonthlyWriteOffs(new Date().getMonth() + 1, new Date().getFullYear())
      ]);
      setStats({
        totalVehicles: vehicles.totalVehicles ?? vehicles.total ?? 0,
        totalOutstanding: outstanding.totalOutstandingBalance ?? outstanding.total ?? 0,
        monthlyInvoices: invoices.totalInvoiceAmount ?? invoices.count ?? 0,
        monthlyPayments: payments.totalPaymentAmount ?? payments.count ?? 0,
        monthlyWriteOffs: writeOffs.totalWriteOffAmount ?? writeOffs.count ?? 0
      });
    } catch (error) {
      console.error("Failed to fetch dashboard stats:", error);
      showError("Failed to load dashboard statistics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const canAccessAction = (action: QuickAction) => {
    if (!action.roles || action.roles.length === 0) return true;
    return user && action.roles.includes(user.role);
  };

  const filteredActions = quickActions.filter(canAccessAction);

  return (
    <div className="min-h-screen w-full bg-gray-50">
      <Header title="Dashboard" showBackButton={false} />
      
      <main className="w-full max-w-7xl mx-auto p-4 space-y-8">
        {/* Quick Actions */}
        <div>
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {filteredActions.map((action, index) => (
              <Card 
                key={index} 
                hover 
                className="cursor-pointer p-4 md:p-6"
                onClick={() => navigate(action.path)}
              >
                <div className="flex items-center space-x-4">
                  <div className={`${action.color} text-white p-3 rounded-lg text-xl flex-shrink-0`}>
                    {action.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-800 truncate">{action.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{action.description}</p>
                  </div>
                  <div className="text-gray-400 flex-shrink-0">→</div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </main>
      
      <ToastContainer />
    </div>
  );
};

export default DashboardImproved;